// EmailDataTables.jsx
import {
	Tabs,
	Tab,
	Box,
	Button,
	Tooltip,
	styled,
	tooltipClasses,
	useTheme,
	Alert,
	Typography,
} from '@mui/material';
import { GridOverlay } from '@mui/x-data-grid-premium';
import { LoadingButton } from '@mui/lab';
import { authedApiFetch } from '../../utils/Api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {
	DataGridPremium,
	GridEditInputCell,
	GridFooterContainer,
} from '@mui/x-data-grid-premium';
import {
	useFetcher,
	useLoaderData,
	useNavigate,
	useSearchParams,
	useOutletContext,
} from 'react-router';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import JobDetailsHeader from './JobDetailsHeader';
import JobStatisticsHeader from './JobStatisticsHeader';

export const clientLoader = async ({ params, request }) => {
	const { jobId } = params;

	// extracting type from search params
	const url = new URL(request.url);
	const searchParams = url.searchParams;
	const type = searchParams.get('type') || 'contacts'; // default to contacts
	const nextToken = searchParams.get('next_token') || null;

	const apiParams = {
		type,
	};

	if (nextToken) {
		apiParams.next_token = nextToken; // string, will be parsed by the server
	}

	const records = await authedApiFetch({
		endpoint: `/email/jobs/${jobId}/records`,
		method: 'GET',
		params: apiParams,
	});

	return {
		job: records.job,
		records: records.records,
		nextToken: records.next_token,
		primaryKey: records.primary_key,
		jobStatus: records.jobStatus,
	};
};

// StyledTooltip, EditInputCell, and renderEditCell adapted from:
// https://mui.com/x/react-data-grid/editing/#validation

const StyledTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.error.contrastText,
	},
}));

function EditInputCell(props) {
	const { error } = props;

	return (
		<StyledTooltip open={!!error} title={error}>
			<GridEditInputCell {...props} />
		</StyledTooltip>
	);
}

EditInputCell.propTypes = {
	error: PropTypes.string,
};

function renderEditCell(params) {
	return <EditInputCell {...params} />;
}

const columnDefinitions = {
	accounts: [
		{ field: 'domain', headerName: 'Domain', editable: false, flex: 1 },
		{
			field: 'company_name',
			headerName: 'Company Name',
			editable: false,
			flex: 1,
		},
		{
			field: 'account_record_type_name',
			headerName: 'Record Type',
			editable: false,
			flex: 1,
		},
		{
			field: 'industry_sector',
			headerName: 'Industry',
			editable: true,
			flex: 1,
		},
		{
			field: 'company_size',
			headerName: 'Company Size',
			editable: true,
			flex: 1,
		},
		{
			field: 'year_founded',
			headerName: 'Year Founded',
			editable: true,
			flex: 1,
		},
		{
			field: 'headquarters_location',
			headerName: 'HQ Location',
			editable: true,
			flex: 1,
		},
		{ field: 'website', headerName: 'Website', editable: true, flex: 1 },
	],
	contacts: [
		{ field: 'contact_email', headerName: 'Email', editable: false, flex: 1 },
		{ field: 'first_name', headerName: 'First Name', editable: true, flex: 1 },
		{
			field: 'last_name',
			headerName: 'Last Name',
			editable: true,
			flex: 1,
			preProcessEditCellProps: (params) => {
				const hasError = params.props.value.length === 0;
				return {
					...params.props,
					error: hasError ? 'Last name is required' : null,
				};
			},
			renderEditCell: renderEditCell,
		},
		{
			field: 'account_name',
			headerName: 'Account',
			editable: false,
			flex: 1,
		},
	],
	interactions: [
		{ field: 'id', headerName: 'Import ID', editable: false, flex: 1 },
		{ field: 'type', headerName: 'Type', editable: false, flex: 1 },
		{
			field: 'name',
			headerName: 'Name',
			editable: true,
			flex: 2,
			preProcessEditCellProps: (params) => {
				const hasError = params.props.value.length === 0;
				return {
					...params.props,
					error: hasError ? 'Name is required' : null,
				};
			},
			renderEditCell: renderEditCell,
		},
		{
			field: 'date',
			headerName: 'Date',
			type: 'date',
			editable: false,
			valueGetter: (value) => value && new Date(value),
			flex: 1,
		},
		{
			field: 'time',
			headerName: 'Time',
			editable: false,
			valueGetter: (value) => value && new Date(value).toLocaleTimeString(),
			flex: 1,
		},
	],
	interaction_references: [
		{
			field: 'interaction_id',
			headerName: 'Interaction ID',
			editable: false,
			flex: 1,
		},
		{
			field: 'account_name',
			headerName: 'Account',
			editable: false,
			flex: 1,
		},
		{
			field: 'contact_email',
			headerName: 'Contact Email',
			editable: false,
			flex: 1,
		},
		{
			field: 'user_sf_username_in_client_org',
			headerName: 'User SF Username',
			editable: false,
			flex: 1,
			renderCell: (params) => (
				<Box
					sx={{
						display: 'flex',
						columnGap: 1,
						alignItems: 'center',
					}}
				>
					<span>
						{![null, undefined].includes(
							params.row.user_sf_username_in_client_org
						)
							? params.row.user_sf_username_in_client_org
							: params.row.user_sf_id}
					</span>
				</Box>
			),
		},
	],
	interaction_messages: [
		{
			field: 'interaction_id',
			headerName: 'Interaction ID',
			editable: false,
			flex: 1,
		},
		{
			field: 'email_body',
			headerName: 'Email Body',
			editable: false,
			flex: 1,
			description:
				'Email Body is conditionally synced to the CRM based on the "Include Email Body in CRM Sync" setting.',
		},
		{
			field: 'message_date',
			headerName: 'Message Date',
			editable: false,
			flex: 1,
			type: 'date',
			valueGetter: (value) => value && new Date(value),
		},
		{
			field: 'message_direction',
			headerName: 'Message Direction',
			editable: false,
			flex: 1,
		},
		{
			field: 'from_address',
			headerName: 'From Address',
			editable: false,
			flex: 1,
		},
		{
			field: 'to_addresses',
			headerName: 'To Addresses',
			editable: false,
			flex: 1,
		},
		{
			field: 'cc_addresses',
			headerName: 'CC Addresses',
			editable: false,
			flex: 1,
		},
	],
};

const sfStatusColumns = [
	{
		field: 'SF_Sync_Status',
		headerName: 'Sync Status',
		editable: false,
		flex: 1,
	},
	{
		field: 'SF_Failed_Error',
		headerName: 'Sync Status Detail',
		editable: false,
		flex: 1,
	},
];

// Tab type mapping
const tabTypes = [
	'contacts',
	'accounts',
	'interactions',
	'interaction_references',
	'interaction_messages',
];

function LoadMoreFooter({ hasMore, onLoadMore, loading, count }) {
	return (
		<GridFooterContainer
			sx={{
				display: 'flex',
				justifyContent: 'flex-end',
				alignItems: 'center',
				width: '100%',
				px: 2,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				<Box>
					{count}
					{hasMore && '+ '} records
				</Box>
				{hasMore && (
					<LoadingButton
						loading={loading}
						size='small'
						variant='contained'
						onClick={onLoadMore}
						style={{ margin: 'auto' }}
					>
						Load More
					</LoadingButton>
				)}
			</Box>
		</GridFooterContainer>
	);
}

// Add prop types validation for the LoadMoreFooter component
LoadMoreFooter.propTypes = {
	hasMore: PropTypes.bool,
	onLoadMore: PropTypes.func,
	loading: PropTypes.bool,
	count: PropTypes.number,
};

const EmailDataTables = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const {
		job,
		records: initialRecords,
		nextToken: initialNextToken,
		primaryKey,
		jobStatus,
	} = useLoaderData();
	const navigate = useNavigate();
	// Get outlet context with showNotification
	const { showNotification } = useOutletContext();

	const [records, setRecords] = useState(initialRecords);
	const [nextToken, setNextToken] = useState(initialNextToken);

	const fetcher = useFetcher();
	const updateFetcher = useFetcher();

	const theme = useTheme();
	const isDark = theme.palette.mode === 'dark';

	// Prevent editing when job is synced or syncing
	const isEditable = !['synced', 'syncing'].includes(jobStatus);

	// Get the current tab/type from searchParams
	const type = searchParams.get('type') || 'contacts';

	const handleTabChange = (_event, newValue) => {
		// Change tab and reset table-related search params in one go,
		// otherwise params carry over between tables and error on different columns
		setSearchParams(new URLSearchParams({ type: tabTypes[newValue] }), {
			replace: true,
		});
	};

	// Handler to load more rows
	const handleLoadMore = async () => {
		if (!nextToken) {
			return;
		}
		fetcher.load(
			`./?type=${type}&next_token=${encodeURIComponent(JSON.stringify(nextToken))}`
		);
	};

	// When the tab/type changes, reset records and nextToken to loader data
	useEffect(() => {
		setRecords(initialRecords);
		setNextToken(initialNextToken);
	}, [type, initialRecords, initialNextToken]);

	// When fetcher gets new data (from "Load More"), append it
	useEffect(() => {
		if (fetcher.data) {
			const { records: newRecords, nextToken: newNextToken } = fetcher.data;
			setRecords((prev) => [...prev, ...newRecords]);
			setNextToken(newNextToken);
		}
	}, [fetcher.data]);

	// Generate columns for a given type
	const columnsForType = (type) => {
		// Get base columns based on the current type
		const baseColumns = columnDefinitions[type] || [];

		// Add SF status columns
		const columns = [...baseColumns, ...sfStatusColumns];

		// Add common properties to all columns
		const finalCols = columns.map((col) => ({
			...col,
			minWidth: 100,
			maxWidth: 500,
			filterable: false,
		}));
		return finalCols;
	};

	const activeTab = tabTypes.indexOf(searchParams.get('type') || 'contacts');
	const currentType = tabTypes[activeTab];
	const columns = columnsForType(currentType);

	// Track fetcher state changes for notifications
	useEffect(() => {
		if (updateFetcher.state === 'idle' && updateFetcher.data) {
			const { success, message } = updateFetcher.data;

			// Show notification based on success/failure
			if (success) {
				showNotification(message || 'Record updated successfully', 'success');
			} else {
				showNotification(message || 'Failed to update record', 'error');
			}
		}
		// Including showNotifications causes an infinite loop when leaving page.
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateFetcher.state, updateFetcher.data]);

	// Handle row updates
	const handleRowUpdate = async (updatedRow, originalRow) => {
		// Find all keys that changed
		const changedAttributes = {};
		Object.keys(updatedRow).forEach((key) => {
			if (updatedRow[key] !== originalRow[key]) {
				changedAttributes[key] = updatedRow[key];
			}
		});

		// If no changes, return early
		if (Object.keys(changedAttributes).length === 0) {
			console.warn('No changes detected between updated and original row');
			return updatedRow;
		}

		if (!primaryKey) {
			console.error('Primary key not found in loader data');
			return originalRow;
		}

		if (!updatedRow[primaryKey]) {
			console.error(`Row is missing the primary key value: ${primaryKey}`);
			return originalRow;
		}

		try {
			// Use fetcher to submit the form
			await updateFetcher.submit(
				{
					recordType: currentType,
					recordId: updatedRow[primaryKey],
					updatedData: JSON.stringify(changedAttributes),
				},
				{
					method: 'post',
					action: 'update',
				}
			);

			// Return the updated row
			return updatedRow;
		} catch (error) {
			console.error('Failed to update row:', error);

			// Show error notification directly in the catch block
			showNotification(
				'Failed to update record: ' + (error.message || 'Unknown error'),
				'error'
			);

			// Return the original row on error to revert changes
			return originalRow;
		}
	};

	// Custom component for empty state
	// If job is ingested or synced, but there are no rows, it's possible that the job data is deleted after 60 days
	const CustomNoRowsOverlay = () => {
		const isMoreThan60DaysOld = () => {
			if (!job?.created_at) return false;
			const createdDate = new Date(job.created_at);
			const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
			return createdDate < sixtyDaysAgo;
		};

		if (
			(jobStatus === 'ingested' || jobStatus === 'synced') &&
			isMoreThan60DaysOld()
		) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						padding: '20px',
						textAlign: 'center',
					}}
				>
					<Typography>
						Email Sync data is automatically removed 60 days after ingestion to
						comply with Altvia’s data retention policy.
					</Typography>
				</Box>
			);
		}

		// Default empty state
		return (
			<GridOverlay
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Typography>No rows</Typography>
			</GridOverlay>
		);
	};

	return (
		<Box
			sx={{
				py: 0,
				pt: 1.5,
				width: '100%',
				height: 'calc(100vh - 64px)',
				overflow: 'auto',
			}}
		>
			<Button
				variant='contained'
				startIcon={<ArrowBackIcon />}
				onClick={() => navigate(-1)}
				sx={{ mb: 2 }}
			>
				Back to Email Jobs
			</Button>

			{job && <JobDetailsHeader job={job} />}
			{job && <JobStatisticsHeader job={job} />}

			<Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
				<Tab label='Contacts' />
				<Tab label='Accounts' />
				<Tab label='Interactions' />
				<Tab label='Interaction References' />
				<Tab label='Interaction Messages' />
			</Tabs>

			{isEditable && (
				<Alert
					severity='info'
					sx={{ mb: 2, backgroundColor: theme.palette.background.lightBlue }}
				>
					Non-italicized columns can be edited. Double-click the cell to enter
					edit mode, and press Enter to save.
				</Alert>
			)}

			<Box
				component='main'
				sx={{
					overflowY: 'auto',
					display: 'flex',
					flexDirection: 'column',
					rowGap: 1,
					height: '50vh',
					'& .MuiDataGrid-cell': {
						fontStyle: 'italic',
					},
					'& .MuiDataGrid-cell--editable': {
						fontStyle: 'normal',
						backgroundColor: isDark
							? theme.palette.background.lightPurple
							: theme.palette.background.paper,
					},
				}}
			>
				<DataGridPremium
					editMode='row'
					rows={records}
					columns={columns}
					isCellEditable={() => isEditable}
					processRowUpdate={handleRowUpdate}
					pagination={false}
					// Show loading overlay when updates are being submitted
					loading={updateFetcher.state !== 'idle'}
					slots={{
						footer: LoadMoreFooter,
						noRowsOverlay: () => <CustomNoRowsOverlay />,
					}}
					slotProps={{
						footer: {
							hasMore: Boolean(nextToken),
							onLoadMore: handleLoadMore,
							loading: fetcher.state === 'loading',
							count: records.length,
						},
					}}
				/>
			</Box>
		</Box>
	);
};

export default EmailDataTables;
